import { coerceBooleanProperty } from '@angular/cdk/coercion';
import { NgIf } from '@angular/common';
import { AfterContentInit, Component, HostBinding, Input, Optional, Self, ViewEncapsulation } from '@angular/core';
import {
  AbstractControl,
  ControlValueAccessor,
  NgControl,
  ReactiveFormsModule,
  UntypedFormBuilder,
  UntypedFormGroup,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { DisableTabDirective } from 'cloud-shared-lib';

export interface IElasticValueControlData {
  min: number;
  max: number;
  step: number;
}

@Component({
  selector: 'cutting-elastic-value-control',
  templateUrl: './elastic-value-control.component.html',
  styleUrls: ['./elastic-value-control.component.scss'],
  standalone: true,
  imports: [NgIf, MatSliderModule, MatFormFieldModule, ReactiveFormsModule, MatInputModule, DisableTabDirective],
  encapsulation: ViewEncapsulation.None,
})
export class ElasticValueControlComponent implements ControlValueAccessor, AfterContentInit {
  static nextId = 0;

  @HostBinding() id = `elastic-value-control-component-${ElasticValueControlComponent.nextId++}`;

  @HostBinding('class.elastic-value-control')
  elasticValueControl = true;

  elasticControlForm: UntypedFormGroup;

  @Input()
  get constraint(): IElasticValueControlData {
    return this.limit;
  }
  set constraint(val: IElasticValueControlData) {
    this.limit = val;

    this.elasticControlForm.controls.input.setValidators([
      Validators.required,
      Validators.min(val.min),
      Validators.max(val.max),
      this.stepValidator(val.step, val.min),
    ]);

    if (this.elasticControlForm) {
      this.elasticControlForm.updateValueAndValidity();
    }

    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.updateValueAndValidity();
    }

    // this.stateChanges.next();
  }
  private limit: IElasticValueControlData = { min: 0, max: 0, step: 0 };

  @Input()
  get required(): boolean {
    return this.requested;
  }
  set required(req) {
    this.requested = coerceBooleanProperty(req);
    // this.stateChanges.next();
  }
  private requested = false;

  @Input()
  get disabled(): boolean {
    return this.disable;
  }
  set disabled(value: boolean) {
    this.disable = coerceBooleanProperty(value);
    if (this.disable) {
      this.elasticControlForm.disable();
    } else {
      this.elasticControlForm.enable();
    }
    // this.stateChanges.next();
  }
  private disable = false;

  get empty(): boolean {
    const n = this.elasticControlForm.value;
    return !n.input && !n.slider;
  }

  constructor(fb: UntypedFormBuilder, @Optional() @Self() public ngControl: NgControl) {
    this.elasticControlForm = fb.group({
      slider: 0,
      input: 0,
    });

    // Replace the provider from above with this.
    if (this.ngControl != null) {
      // Setting the value accessor directly (instead of using
      // the providers) to avoid running into a circular import.
      this.ngControl.valueAccessor = this;
    }
  }

  ngAfterContentInit(): void {
    this.ngControl.control.setValidators(this.mainValidator());
    this.ngControl.control.updateValueAndValidity();
    this.elasticControlForm.updateValueAndValidity();

    this.elasticControlForm.controls.slider.valueChanges.subscribe((v: number) => {
      this.onChange(v);
      this.elasticControlForm.controls.input.setValue(v, { emitEvent: false });

      this.elasticControlForm.updateValueAndValidity();
    });

    this.elasticControlForm.controls.input.valueChanges.subscribe((v: number) => {
      if (this.elasticControlForm.controls.input.valid) {
        this.elasticControlForm.controls.slider.setValue(v);
      }

      this.ngControl.control.updateValueAndValidity();
    });
  }

  stepValidator(step: number, minValue: number): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const forbidden = (+control.value - minValue) % step > 0;
      return forbidden ? { forbiddenStep: { value: control.value, step: step } } : null;
    };
  }

  mainValidator(): ValidatorFn {
    return (): { [key: string]: any } | null => this.elasticControlForm.controls.input.errors;
  }

  writeValue(val: number): void {
    this.elasticControlForm.setValue({ slider: val, input: val });

    if (this.elasticControlForm) {
      this.elasticControlForm.updateValueAndValidity();
    }

    if (this.ngControl && this.ngControl.control) {
      this.ngControl.control.updateValueAndValidity();
    }
  }

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onChange(_: any): void {}

  // eslint-disable-next-line @typescript-eslint/no-empty-function
  onTouch: any = () => {};

  registerOnTouched(fn: any): void {
    this.onTouch = fn;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }
}
